import { useEffect, useState } from 'react';
import { api, errMsg } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { CreditCard, Loader2, ExternalLink, Sparkles, Info, RefreshCw, Gift } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const PLAN_LABEL = { free: 'No subscription', try: 'Try', starter: 'Starter', growth: 'Growth', enterprise: 'Enterprise' };

export default function BillingCard() {
  const [billing, setBilling] = useState(null);
  const [loading, setLoading] = useState(true);
  const [portalLoading, setPortalLoading] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/billing/me');
      setBilling(r.data);
    } catch (_e) {
      // silent
    } finally {
      setLoading(false);
    }
  };

  const sync = async (silent = true) => {
    try {
      const r = await api.post('/billing/sync');
      setBilling(r.data);
      return r.data;
    } catch (_e) {
      if (!silent) toast.error('Could not reach Dodo Payments to check your subscription. Please try again shortly.');
      return null;
    }
  };

  const [syncing, setSyncing] = useState(false);
  const refreshStatus = async () => {
    setSyncing(true);
    const before = billing?.plan;
    const r = await sync(false);
    setSyncing(false);
    if (r && r.plan !== before && r.plan !== 'free') {
      toast.success(`Subscription synced — you're on the ${PLAN_LABEL[r.plan] || r.plan} plan.`);
    } else if (r) {
      toast.success('Billing status refreshed.');
    }
  };

  useEffect(() => {
    load();
    // If user just returned from a successful Dodo checkout, actively
    // reconcile against Dodo (self-heals missed/delayed webhooks) and poll.
    const params = new URLSearchParams(window.location.search);
    if (params.get('billing') === 'success') {
      toast.success('Payment received — your plan will activate momentarily.');
      sync();
      const t = setInterval(sync, 3000);
      setTimeout(() => clearInterval(t), 30000);
    } else {
      // Passive reconciliation on every settings visit so a missed webhook
      // never permanently strands a paying customer on "Free".
      sync();
    }
  }, []);

  const openPortal = async () => {
    setPortalLoading(true);
    try {
      const r = await api.post('/billing/portal');
      if (r.data?.portal_url) window.location.href = r.data.portal_url;
    } catch (err) {
      toast.error(errMsg(err, 'Could not open portal'));
    } finally {
      setPortalLoading(false);
    }
  };

  const cancelSub = async () => {
    if (!window.confirm('Cancel at end of current period? You keep access until then.')) return;
    setCancelLoading(true);
    try {
      await api.post('/billing/cancel', { confirm: true });
      toast.success('Cancellation scheduled at end of period');
      await load();
    } catch (err) {
      toast.error(errMsg(err, 'Cancel failed'));
    } finally {
      setCancelLoading(false);
    }
  };

  const plan = billing?.plan || 'free';
  const planLabel = PLAN_LABEL[plan] || plan;
  const status = billing?.status || 'active';
  const configured = billing?.billing_configured;
  const isTrial = !!billing?.is_trial;
  const planPrice = billing?.plan_details?.price_usd;

  return (
    <Card className="border p-6 space-y-4" data-testid="settings-billing-card">
      <div className="flex items-center gap-2">
        <CreditCard className="h-4 w-4 text-muted-foreground" />
        <div className="font-display text-base font-semibold">Billing & Plan</div>
      </div>

      {loading ? (
        <div className="text-sm text-muted-foreground">Loading…</div>
      ) : (
        <>
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border bg-secondary/40 p-4">
            <div>
              <div className="text-xs uppercase tracking-wide text-muted-foreground">Current plan</div>
              <div className="mt-1 flex items-center gap-2">
                <div className="font-display text-xl font-semibold" data-testid="settings-billing-plan-name">{planLabel}</div>
                {isTrial ? (
                  <Badge className="gap-1 bg-[hsl(var(--accent))]/12 text-[hsl(var(--accent))] border border-[hsl(var(--accent))]/30" data-testid="settings-billing-trial-badge">
                    <Gift className="h-3 w-3" /> Free trial
                  </Badge>
                ) : (
                  <Badge variant={status === 'active' ? 'default' : status === 'cancelled' ? 'destructive' : 'secondary'} className="capitalize">{status.replace('_', ' ')}</Badge>
                )}
                {billing?.cancel_at_period_end && (
                  <Badge variant="outline" className="text-[11px]">Cancels at period end</Badge>
                )}
              </div>
              {isTrial && billing?.trial_end ? (
                <div className="text-xs text-muted-foreground mt-1" data-testid="settings-billing-trial-line">
                  Free trial ends {String(billing.trial_end).slice(0, 10)}
                  {planPrice ? ` — then $${planPrice}/mo` : ''}. Cancel anytime before then to avoid charges.
                </div>
              ) : billing?.current_period_end && (
                <div className="text-xs text-muted-foreground mt-1">Renews / ends: {String(billing.current_period_end).slice(0, 10)}</div>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Button variant="ghost" size="sm" onClick={refreshStatus} disabled={syncing} data-testid="settings-refresh-billing-button" title="Re-check subscription status against Dodo Payments">
                <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${syncing ? 'animate-spin' : ''}`} /> Refresh status
              </Button>
              <Button variant="outline" size="sm" onClick={() => navigate('/app/billing/upgrade')} data-testid="settings-view-plans-button">
                View plans
              </Button>
              {plan === 'free' ? (
                <Button size="sm" onClick={() => navigate('/app/billing/upgrade')} data-testid="settings-upgrade-button">
                  <Sparkles className="h-3.5 w-3.5 mr-1.5" /> Upgrade
                </Button>
              ) : (
                <>
                  {billing?.customer_id && (
                    <Button size="sm" variant="outline" onClick={openPortal} disabled={portalLoading} data-testid="settings-portal-button">
                      {portalLoading ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <ExternalLink className="h-3.5 w-3.5 mr-1.5" />}
                      Manage in portal
                    </Button>
                  )}
                  {!billing?.cancel_at_period_end && billing?.subscription_id && (
                    <Button size="sm" variant="ghost" onClick={cancelSub} disabled={cancelLoading} data-testid="settings-cancel-sub-button">
                      {cancelLoading && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />} Cancel
                    </Button>
                  )}
                </>
              )}
            </div>
          </div>

          {!configured && (
            <div className="rounded-md border bg-amber-50 border-amber-200 text-amber-900 px-3 py-2 text-xs flex items-start gap-2">
              <Info className="h-3.5 w-3.5 mt-0.5" />
              <div>
                <div className="font-medium">Payments aren&apos;t live yet on this deployment</div>
                <div className="opacity-90 mt-0.5">Add your Dodo Payments API key & product IDs to enable real checkout. Everything else works today.</div>
              </div>
            </div>
          )}
        </>
      )}
    </Card>
  );
}
